import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useAppStore } from '../state/store';
import type { OhlcByInstrument } from '../state/store';
import { getCanonicalTimestampsForAllCharts, getSplitChartData, padSeriesToTimeline } from '../lib/chartTimeline';
import { useSyncedCrosshair } from '../hooks/useSyncedCrosshair';
import { splitAnnotationPlugin } from './splitAnnotationPlugin';
import {
  buildUplotData,
  chartPanPlugin,
  CHART_RIGHT_AXIS_SIZE,
  createXAxisConfig,
  findIndexForTimestamp,
  getChartCursorSyncConfig,
  ohlcBarsPlugin,
  visiblePriceRange,
  VOLUME_STRIP_RATIO,
  visibleVolRange,
  xAxisSecondRowPlugin,
} from './PrimarySeriesChart';

const CHART_HEIGHT = 400;
const ADDITIONAL_UP_COLOR = '#2a3a1a';
const ADDITIONAL_DOWN_COLOR = '#6a2a2a';

function formatLegendDate(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return '';
  const d = new Date(sec * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

type Props = {
  instrumentId: string;
  symbol: string;
};

export default function AdditionalInstrumentChart({ instrumentId, symbol }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const timestampsRef = useRef<number[]>([]);

  const ohlc = useAppStore((s) => s.ohlc);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const backtestEquityCurve = useAppStore((s) => s.backtestEquityCurve);
  const chartTimeSync = useAppStore((s) => s.chartTimeSync);
  const primaryChartFullWidth = useAppStore((s) => s.primaryChartFullWidth);
  const additionalChartDisplayAdjusted = useAppStore((s) => s.additionalChartDisplayAdjusted);
  const setAdditionalChartDisplayAdjusted = useAppStore((s) => s.setAdditionalChartDisplayAdjusted);

  const chartId = `additional-${instrumentId}` as const;
  useSyncedCrosshair(
    chartId,
    chartRef,
    timestampsRef,
    (ts) => findIndexForTimestamp(timestampsRef.current, ts ?? 0) ?? undefined
  );

  useEffect(() => {
    if (!containerRef.current || !ohlc || !primaryInstrumentId) return;

    const instrumentAdjusted = instruments.find((i) => i.id === instrumentId)?.adjusted ?? true;
    const displayAdjusted = additionalChartDisplayAdjusted[instrumentId] ?? instrumentAdjusted;
    const raw = buildUplotData(ohlc as OhlcByInstrument, instrumentId, displayAdjusted);
    if (!raw || raw.data[0].length === 0) return;

    const instrumentIds = instruments.map((i) => i.id);
    const equityTs =
      backtestEquityCurve?.timestamps.map((ts) => {
        const s = typeof ts === 'string' ? ts.slice(0, 10) : '';
        return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
      }) ?? [];
    const timestamps = getCanonicalTimestampsForAllCharts(
      ohlc,
      primaryInstrumentId,
      instrumentIds,
      equityTs.length > 0 ? equityTs : undefined
    );
    if (timestamps.length === 0) return;

    timestampsRef.current = timestamps;

    const splitChartData =
      !displayAdjusted ? getSplitChartData(ohlc, instrumentId, timestamps) : [];

    const open = padSeriesToTimeline(
      timestamps,
      raw.timestamps,
      raw.data[1] as number[],
      null
    );
    const high = padSeriesToTimeline(
      timestamps,
      raw.timestamps,
      raw.data[2] as number[],
      null
    );
    const low = padSeriesToTimeline(
      timestamps,
      raw.timestamps,
      raw.data[3] as number[],
      null
    );
    const close = padSeriesToTimeline(
      timestamps,
      raw.timestamps,
      raw.data[4] as number[],
      null
    );
    const vol = padSeriesToTimeline(
      timestamps,
      raw.timestamps,
      raw.data[5] as number[],
      null
    );
    const data: uPlot.AlignedData = [
      Array.from({ length: timestamps.length }, (_, i) => i),
      open,
      high,
      low,
      close,
      vol,
    ];

    const n = data[0].length - 1;
    const sync = useAppStore.getState().chartTimeSync;
    let xMin = 0;
    let xMax = Math.max(0, n);
    if (sync.xMin != null && sync.xMax != null) {
      xMin = Math.max(0, Math.min(sync.xMin, n));
      xMax = Math.max(0, Math.min(sync.xMax, n));
      if (xMin >= xMax) {
        xMin = 0;
        xMax = n;
      }
    }

    const fullW = useAppStore.getState().primaryChartFullWidth;
    const width =
      fullW != null ? fullW : Math.max(containerRef.current.clientWidth, 100);

    const opts: uPlot.Options = {
      width,
      height: CHART_HEIGHT,
      plugins: [
        ohlcBarsPlugin({
          upColor: ADDITIONAL_UP_COLOR,
          downColor: ADDITIONAL_DOWN_COLOR,
        }),
        ...(splitChartData.length > 0
          ? [
              splitAnnotationPlugin({
                getSplitData: () => splitChartData,
                volumeStripRatio: VOLUME_STRIP_RATIO,
              }),
            ]
          : []),
        chartPanPlugin(
          (min, max) => useAppStore.getState().setChartTimeRange(min, max),
          (u, newMin, newMax) => {
            const [yMin, yMax] = visiblePriceRange(u, undefined, undefined, newMin, newMax);
            u.setScale('y', { min: yMin, max: yMax });
            const [, volMax] = visibleVolRange(u, undefined, undefined, newMin, newMax);
            u.setScale('vol', { min: 0, max: volMax });
          },
          getChartCursorSyncConfig(chartId, (_u, idx) => timestamps[idx] ?? null)
        ),
        xAxisSecondRowPlugin(timestamps),
      ],
      scales: {
        x: { min: xMin, max: xMax, distr: 2 },
        y: {
          auto: true,
          range: (u, dataMin, dataMax) => visiblePriceRange(u, dataMin, dataMax),
        },
        vol: {
          auto: true,
          range: (u, _dataMin, dataMax) => visibleVolRange(u, _dataMin, dataMax),
        },
      },
      series: [
        {
          label: 'Date',
          value: (_u, _rawValue, _seriesIdx, idx) =>
            idx != null ? formatLegendDate(timestamps, idx) : '--',
        },
        { label: 'Open', scale: 'y' },
        { label: 'High', scale: 'y' },
        { label: 'Low', scale: 'y' },
        { label: 'Close', scale: 'y' },
        { label: 'Volume', scale: 'vol' },
      ],
      axes: [
        createXAxisConfig(timestamps),
        {
          side: 1,
          scale: 'y',
          size: CHART_RIGHT_AXIS_SIZE,
          values: (_u, vals) => vals.map((v) => (v != null ? v.toFixed(2) : '')),
        },
      ],
    };

    const chart = new uPlot(opts, data, containerRef.current);
    chartRef.current = chart;

    const [yMin, yMax] = visiblePriceRange(chart, undefined, undefined, xMin, xMax);
    const [, volMax] = visibleVolRange(chart, undefined, undefined, xMin, xMax);
    chart.setScale('y', { min: yMin, max: yMax });
    chart.setScale('vol', { min: 0, max: volMax });

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w =
        useAppStore.getState().primaryChartFullWidth ??
        containerRef.current.clientWidth;
      chartRef.current.setSize({ width: w, height: CHART_HEIGHT });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.destroy();
      chartRef.current = null;
    };
  }, [instrumentId, ohlc, primaryInstrumentId, instruments, backtestEquityCurve, additionalChartDisplayAdjusted]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || primaryChartFullWidth == null) return;
    chart.setSize({ width: primaryChartFullWidth, height: CHART_HEIGHT });
  }, [primaryChartFullWidth]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartTimeSync.xMin == null || chartTimeSync.xMax == null) return;
    const n = (chart.data[0] as number[]).length - 1;
    const xMin = Math.max(0, Math.min(chartTimeSync.xMin, n));
    const xMax = Math.max(0, Math.min(chartTimeSync.xMax, n));
    if (xMin >= xMax) return;
    if (chart.scales.x.min === xMin && chart.scales.x.max === xMax) return;
    chart.batch(() => {
      chart.setScale('x', { min: xMin, max: xMax });
      const [yMin, yMax] = visiblePriceRange(chart, undefined, undefined, xMin, xMax);
      chart.setScale('y', { min: yMin, max: yMax });
      const [, volMax] = visibleVolRange(chart, undefined, undefined, xMin, xMax);
      chart.setScale('vol', { min: 0, max: volMax });
    });
  }, [chartTimeSync.xMin, chartTimeSync.xMax]);

  const hasData =
    ohlc?.[instrumentId] && Object.keys(ohlc[instrumentId].series ?? {}).length > 0;
  if (!hasData) return null;

  const displayAdjusted = additionalChartDisplayAdjusted[instrumentId] ?? (instruments.find((i) => i.id === instrumentId)?.adjusted ?? true);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-ink/80">{symbol}</p>
        <div className="flex items-center gap-1">
          <span className="text-xs text-ink/70">Prices:</span>
          {(['adjusted', 'unadjusted'] as const).map((mode) => {
            const isAdjusted = mode === 'adjusted';
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setAdditionalChartDisplayAdjusted(instrumentId, isAdjusted)}
                className={
                  'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                  (displayAdjusted === isAdjusted
                    ? 'bg-ink text-newsprint border-ink'
                    : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
                }
              >
                {mode === 'adjusted' ? 'Adjusted' : 'Unadjusted'}
              </button>
            );
          })}
        </div>
      </div>
      <div
        ref={containerRef}
        className="w-full"
        style={{
          height: CHART_HEIGHT,
          ...(primaryChartFullWidth != null && { width: primaryChartFullWidth }),
        }}
      />
    </div>
  );
}
